import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { makeRequest } from "../../utils/request";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type ExperimentVariantSummary = {
  name: string;
  sessionCount: number;
};

export type ExperimentListItem = {
  name: string;
  sessionCount: number;
  completed: boolean;
  conversionMetrics: string[];
  variants: ExperimentVariantSummary[];
  stopConditions: {
    max_iterations: number;
    current_iterations: number;
    max_confidence: number;
    current_confidence: number;
    target_outcome: string;
    run_test: boolean;
    stop_on: number;
  };
  lastUpdated: string;
};

export type VariantConversion = {
  count: number;
  rate: number;
};

export type ExperimentVariantDetail = {
  name: string;
  sessionCount: number;
  conversions: Record<string, VariantConversion>;
  outcomes: Record<string, { type: string; counts: Record<string, number> }>;
};

export type SignificanceResult = {
  significance: string;
  confidence: number;
  pValue: number;
};

export type ExperimentDetail = {
  name: string;
  agentId: string;
  functionId: string | null;
  sessionCount: number;
  completed: boolean;
  conversionMetrics: string[];
  stopConditions: {
    max_iterations: number;
    current_iterations: number;
    max_confidence: number;
    current_confidence: number;
    target_outcome: string;
    run_test: boolean;
    stop_on: number;
  };
  variants: ExperimentVariantDetail[];
  significance: Record<string, Record<string, SignificanceResult>>;
  lastUpdated: string;
};

export type FunnelEntry = {
  variant: string;
  sessions: number;
  metrics: Record<string, { sessions: number; conversions: number; rate: number }>;
  lift: Record<string, number | null>;
};

export type ExperimentFunnel = {
  experimentName: string;
  totalSessions: number;
  conversionMetrics: string[];
  funnel: FunnelEntry[];
};

export type ExperimentTimeseriesResult = {
  resultType: string;
  result: Array<{
    metric: Record<string, string>;
    values?: [number, string][];
  }>;
};

// ---------------------------------------------------------------------------
// State
// ---------------------------------------------------------------------------

export type ExperimentsState = {
  list: ExperimentListItem[];
  listLoading: boolean;
  listError: string | null;

  detail: ExperimentDetail | null;
  detailLoading: boolean;
  detailError: string | null;

  funnel: ExperimentFunnel | null;
  funnelLoading: boolean;
  funnelError: string | null;

  timeseries: ExperimentTimeseriesResult | null;
  timeseriesLoading: boolean;
  timeseriesError: string | null;
};

const initialState: ExperimentsState = {
  list: [],
  listLoading: false,
  listError: null,
  detail: null,
  detailLoading: false,
  detailError: null,
  funnel: null,
  funnelLoading: false,
  funnelError: null,
  timeseries: null,
  timeseriesLoading: false,
  timeseriesError: null,
};

// ---------------------------------------------------------------------------
// Thunks
// ---------------------------------------------------------------------------

export const getExperiments = createAsyncThunk("experiments/getList", async (params: { agentId: string }) => {
  const { data } = await makeRequest<{ data: ExperimentListItem[] }>(`/v1/experiments?agentId=${params.agentId}`);
  return data;
});

export const getExperimentDetail = createAsyncThunk("experiments/getDetail", async (params: { agentId: string; experimentName: string }) => {
  const { data } = await makeRequest<{ data: ExperimentDetail }>(`/v1/experiments/${encodeURIComponent(params.experimentName)}?agentId=${params.agentId}`);
  return data;
});

export const getExperimentFunnel = createAsyncThunk("experiments/getFunnel", async (params: { agentId: string; experimentName: string }) => {
  const { data } = await makeRequest<{ data: ExperimentFunnel }>(
    `/v1/experiments/${encodeURIComponent(params.experimentName)}/funnel?agentId=${params.agentId}`,
  );
  return data;
});

export const getExperimentTimeseries = createAsyncThunk(
  "experiments/getTimeseries",
  async (params: { agentId: string; experimentName: string; metricName: string; startDate: string; endDate: string; step?: string }) => {
    const qs = new URLSearchParams({
      agentId: params.agentId,
      metricName: params.metricName,
      startDate: params.startDate,
      endDate: params.endDate,
    });
    if (params.step) qs.append("step", params.step);

    const { data } = await makeRequest<{ data: ExperimentTimeseriesResult }>(`/v1/experiments/${encodeURIComponent(params.experimentName)}/timeseries?${qs}`);
    return data;
  },
);

// ---------------------------------------------------------------------------
// Slice
// ---------------------------------------------------------------------------

const experimentsSlice = createSlice({
  name: "experiments",
  initialState,
  reducers: {
    clearDetail: (state) => {
      state.detail = null;
      state.detailError = null;
      state.funnel = null;
      state.funnelError = null;
      state.timeseries = null;
      state.timeseriesError = null;
    },
    clearAll: () => initialState,
  },
  extraReducers: (builder) => {
    // List
    builder.addCase(getExperiments.pending, (state) => {
      state.listLoading = true;
      state.listError = null;
    });
    builder.addCase(getExperiments.fulfilled, (state, action) => {
      state.listLoading = false;
      state.list = action.payload;
    });
    builder.addCase(getExperiments.rejected, (state, action) => {
      state.listLoading = false;
      state.listError = action.error.message || "Failed to fetch experiments";
    });

    // Detail
    builder.addCase(getExperimentDetail.pending, (state) => {
      state.detailLoading = true;
      state.detailError = null;
    });
    builder.addCase(getExperimentDetail.fulfilled, (state, action) => {
      state.detailLoading = false;
      state.detail = action.payload;
    });
    builder.addCase(getExperimentDetail.rejected, (state, action) => {
      state.detailLoading = false;
      state.detailError = action.error.message || "Failed to fetch experiment detail";
    });

    // Funnel
    builder.addCase(getExperimentFunnel.pending, (state) => {
      state.funnelLoading = true;
      state.funnelError = null;
    });
    builder.addCase(getExperimentFunnel.fulfilled, (state, action) => {
      state.funnelLoading = false;
      state.funnel = action.payload;
    });
    builder.addCase(getExperimentFunnel.rejected, (state, action) => {
      state.funnelLoading = false;
      state.funnelError = action.error.message || "Failed to fetch funnel data";
    });

    // Timeseries
    builder.addCase(getExperimentTimeseries.pending, (state) => {
      state.timeseriesLoading = true;
      state.timeseriesError = null;
    });
    builder.addCase(getExperimentTimeseries.fulfilled, (state, action) => {
      state.timeseriesLoading = false;
      state.timeseries = action.payload;
    });
    builder.addCase(getExperimentTimeseries.rejected, (state, action) => {
      state.timeseriesLoading = false;
      state.timeseriesError = action.error.message || "Failed to fetch timeseries data";
    });
  },
});

export const { clearDetail, clearAll } = experimentsSlice.actions;
export const experimentsReducer = experimentsSlice.reducer;
